import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBoxOpen,
  FaDownload,
  FaFileAlt,
  FaPaperPlane,
  FaPlus,
  FaSync,
  FaUpload,
} from "react-icons/fa";
import Seo from "../components/Seo";
import {
  CUSTOM_DESIGN_API_BASE,
  formatProjectDate,
  formatUsd,
  humanizeEvent,
  projectStatusLabels,
  quoteTypeLabels,
  readApiError,
} from "./customDesignUtils";
import "./AdminCustomProjects.css";

const statuses = [
  "submitted", "feasibility_review", "design_payment_pending", "modeling",
  "design_review", "prototype_payment_pending", "prototyping", "prototype_shipped",
  "sample_review", "production_payment_pending", "in_production", "completed", "cancelled",
];

const emptyQuote = {
  quoteType: "design",
  notes: "",
  leadTime: "",
  validUntil: "",
  collectShipping: false,
  items: [{ description: "", quantity: 1, amount: "" }],
};

const AdminCustomProjects = () => {
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [messageFile, setMessageFile] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [quote, setQuote] = useState(emptyQuote);
  const [version, setVersion] = useState({ title: "", notes: "", glb: null, renders: [] });
  const [shipping, setShipping] = useState({ trackingCarrier: "Yanwen", trackingNumber: "", trackingUrl: "", shippingNote: "" });
  const [storage, setStorage] = useState(null);

  const headers = useMemo(() => (auth ? { Authorization: `Basic ${auth}` } : {}), [auth]);

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${CUSTOM_DESIGN_API_BASE}${path}`, {
      ...options,
      headers: { ...headers, ...(options.headers || {}) },
    });
    if (!response.ok) throw new Error(await readApiError(response, "Admin request failed."));
    return response.json();
  }, [headers]);

  const loadProjects = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const data = await request("/api/admin/custom-design/projects");
      const items = Array.isArray(data.items) ? data.items : [];
      setProjects(items);
      setStorage(data.storage || null);
      setSelectedId((current) => current || items[0]?.id || "");
    } catch (error) {
      setStatusMessage(error.message || "Failed to load projects.");
      if (/unauthorized/i.test(error.message)) {
        localStorage.removeItem("adminAuth");
        setAuth("");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, request]);

  const loadDetail = useCallback(async (id) => {
    if (!auth || !id) return;
    setDetailLoading(true);
    try {
      const data = await request(`/api/admin/custom-design/projects/${id}`);
      setProject(data.project);
      setShipping({
        trackingCarrier: data.project.trackingCarrier || "Yanwen",
        trackingNumber: data.project.trackingNumber || "",
        trackingUrl: data.project.trackingUrl || "",
        shippingNote: data.project.shippingNote || "",
      });
    } catch (error) {
      setStatusMessage(error.message || "Failed to load project.");
    } finally {
      setDetailLoading(false);
    }
  }, [auth, request]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const syncProject = (updated) => {
    setProject(updated);
    setProjects((current) => current.map((item) => (
      item.id === updated.id ? { ...item, ...updated } : item
    )));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    const encoded = window.btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", encoded);
    setAuth(encoded);
    setLogin({ username: "", password: "" });
  };

  const runAction = async (name, action, successMessage) => {
    setBusy(name);
    setStatusMessage("");
    try {
      const data = await action();
      if (data.project) syncProject(data.project);
      setStatusMessage(successMessage);
      return data;
    } catch (error) {
      setStatusMessage(error.message || "Action failed.");
      return null;
    } finally {
      setBusy("");
    }
  };

  const updateStatus = (status) => runAction(
    "status",
    () => request(`/api/admin/custom-design/projects/${project.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }),
    "Project status updated.",
  );

  const updateQuoteItem = (index, key, value) => {
    setQuote((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      )),
    }));
  };

  const createQuote = async (event) => {
    event.preventDefault();
    const payload = {
      ...quote,
      validUntil: quote.validUntil ? new Date(quote.validUntil).toISOString() : "",
      items: quote.items.map((item) => ({
        description: item.description,
        quantity: Number(item.quantity),
        unitAmountCents: Math.round(Number(item.amount) * 100),
      })),
    };
    const data = await runAction(
      "quote",
      () => request(`/api/admin/custom-design/projects/${project.id}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
      "Quote draft created.",
    );
    if (data) setQuote(emptyQuote);
  };

  const publishQuote = (quoteId) => runAction(
    `publish-${quoteId}`,
    () => request(`/api/admin/custom-design/quotes/${quoteId}/publish`, { method: "POST" }),
    "Quote published and customer notified.",
  );

  const cancelQuote = (quoteId) => runAction(
    `cancel-${quoteId}`,
    () => request(`/api/admin/custom-design/quotes/${quoteId}/cancel`, { method: "POST" }),
    "Quote cancelled.",
  );

  const uploadVersion = async (event) => {
    event.preventDefault();
    const payload = new FormData();
    payload.append("title", version.title);
    payload.append("notes", version.notes);
    if (version.glb) payload.append("glb", version.glb);
    version.renders.forEach((file) => payload.append("renders", file));
    const data = await runAction(
      "version",
      () => request(`/api/admin/custom-design/projects/${project.id}/versions`, {
        method: "POST",
        body: payload,
      }),
      "3D design version published.",
    );
    if (data) setVersion({ title: "", notes: "", glb: null, renders: [] });
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const payload = new FormData();
    payload.append("body", message);
    if (messageFile) payload.append("file", messageFile);
    const data = await runAction(
      "message",
      () => request(`/api/admin/custom-design/projects/${project.id}/messages`, {
        method: "POST",
        body: payload,
      }),
      "Message sent.",
    );
    if (data) {
      setMessage("");
      setMessageFile(null);
    }
  };

  const updateShipping = async (event) => {
    event.preventDefault();
    await runAction(
      "shipping",
      () => request(`/api/admin/custom-design/projects/${project.id}/shipping`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(shipping),
      }),
      "Prototype shipping published.",
    );
  };

  const retryNotification = (notificationId) => runAction(
    `notification-${notificationId}`,
    () => request(`/api/admin/custom-design/notifications/${notificationId}/retry`, { method: "POST" }),
    "Notification retry completed.",
  );

  const downloadFile = async (file) => {
    setBusy(`file-${file.id}`);
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}${file.url}`, { headers });
      if (!response.ok) throw new Error("File download failed.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.originalName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setStatusMessage(error.message || "File download failed.");
    } finally {
      setBusy("");
    }
  };

  return (
    <main className="admin-custom-projects">
      <Seo title="Admin Custom Projects" description="Internal custom project management." path="/admin/custom-projects" noindex />
      <header className="admin-custom-projects__header">
        <div>
          <h1>Custom Design Projects</h1>
          <nav><Link to="/admin">Products</Link><Link to="/admin/orders">Orders</Link><Link to="/admin/custom-requests">Yo-Yo Requests</Link></nav>
        </div>
        {auth ? <button type="button" onClick={() => { localStorage.removeItem("adminAuth"); setAuth(""); }}>Log out</button> : null}
      </header>

      {!auth ? (
        <form className="admin-project-login" onSubmit={handleLogin}>
          <h2>Admin login</h2>
          <label>Username<input type="text" value={login.username} onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))} required /></label>
          <label>Password<input type="password" value={login.password} onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))} required /></label>
          <button type="submit">Log in</button>
        </form>
      ) : (
        <>
          {statusMessage && <div className="admin-project-message">{statusMessage}</div>}
          {storage?.usedPercent >= 80 ? (
            <div className="admin-project-storage-alert">
              Server storage is {storage.usedPercent}% full. Free space: {(storage.freeBytes / 1024 / 1024 / 1024).toFixed(2)} GB.
            </div>
          ) : null}
          <div className="admin-custom-projects__layout">
            <aside className="admin-project-list">
              <header><h2>Projects</h2><button type="button" onClick={loadProjects} disabled={loading}><FaSync /> {loading ? "Loading" : "Refresh"}</button></header>
              {projects.map((item) => (
                <button className={selectedId === item.id ? "selected" : ""} type="button" key={item.id} onClick={() => setSelectedId(item.id)}>
                  <strong>{item.title}</strong><span>{item.customerName || item.customerEmail}</span>
                  <em>{projectStatusLabels[item.status] || item.status}</em><small>{formatProjectDate(item.updatedAt)}</small>
                </button>
              ))}
              {!projects.length && !loading ? <p>No custom design projects.</p> : null}
            </aside>

            <section className="admin-project-detail">
              {detailLoading ? <p>Loading project...</p> : null}
              {!detailLoading && project ? (
                <>
                  <div className="admin-project-title">
                    <div><span>{project.id}</span><h2>{project.title}</h2><p>{project.description}</p></div>
                    <label>Status<select value={project.status} onChange={(event) => updateStatus(event.target.value)} disabled={busy === "status"}>{statuses.map((status) => <option key={status} value={status}>{projectStatusLabels[status] || status}</option>)}</select></label>
                  </div>

                  <div className="admin-project-facts">
                    <div><span>Customer</span><strong>{project.customerName || "Not set"}</strong><a href={`mailto:${project.customerEmail}`}>{project.customerEmail}</a></div>
                    <div><span>Service</span><strong>{project.serviceType.replace(/_/g, " ")}</strong></div>
                    <div><span>Country</span><strong>{project.countryCode}</strong></div>
                    <div><span>Quantity</span><strong>{project.quantity || "Not set"}</strong></div>
                    <div><span>Dimensions</span><strong>{project.dimensions || "Not set"}</strong></div>
                    <div><span>Material</span><strong>{project.material || "Recommendation needed"}</strong></div>
                  </div>

                  <section className="admin-project-panel">
                    <h3>Customer files</h3>
                    <div className="admin-project-files">
                      {project.files.map((file) => (
                        <button type="button" onClick={() => downloadFile(file)} disabled={busy === `file-${file.id}`} key={file.id}>
                          <FaFileAlt /><span><strong>{file.originalName}</strong><small>{(file.sizeBytes / 1024 / 1024).toFixed(2)} MB · {file.kind}</small></span><FaDownload />
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="admin-project-panel">
                    <div className="admin-panel-heading"><h3>Quotes</h3><span>Published quotes are immutable</span></div>
                    <div className="admin-existing-quotes">
                      {project.quotes.map((item) => (
                        <article key={item.id}>
                          <header><div><strong>{quoteTypeLabels[item.quoteType]}</strong><span>v{item.versionNumber}</span></div><em>{item.status}</em></header>
                          <h4>{formatUsd(item.totalCents)}</h4>
                          <ul>{item.items.map((line) => <li key={line.id}><span>{line.description} × {line.quantity}</span><strong>{formatUsd(line.unitAmountCents * line.quantity)}</strong></li>)}</ul>
                          <div>
                            {item.status === "draft" && <button type="button" onClick={() => publishQuote(item.id)} disabled={Boolean(busy)}>Publish quote</button>}
                            {!["paid", "cancelled", "expired"].includes(item.status) && <button className="secondary" type="button" onClick={() => cancelQuote(item.id)} disabled={Boolean(busy)}>Cancel</button>}
                          </div>
                        </article>
                      ))}
                    </div>
                    <form className="admin-quote-form" onSubmit={createQuote}>
                      <h4>Create quote draft</h4>
                      <div className="admin-form-grid">
                        <label>Quote type<select value={quote.quoteType} onChange={(event) => setQuote((current) => ({ ...current, quoteType: event.target.value }))}><option value="design">3D design</option><option value="prototype">Prototype</option><option value="small_batch">Small batch</option><option value="mold_production">Mold & production</option></select></label>
                        <label>Lead time<input type="text" value={quote.leadTime} onChange={(event) => setQuote((current) => ({ ...current, leadTime: event.target.value }))} placeholder="e.g. 5–7 business days" /></label>
                        <label>Valid until<input type="datetime-local" value={quote.validUntil} onChange={(event) => setQuote((current) => ({ ...current, validUntil: event.target.value }))} /></label>
                        <label className="checkbox"><input type="checkbox" checked={quote.collectShipping} onChange={(event) => setQuote((current) => ({ ...current, collectShipping: event.target.checked }))} /> Collect shipping address</label>
                      </div>
                      <div className="admin-quote-items">
                        {quote.items.map((item, index) => (
                          <div key={index}>
                            <input aria-label="Description" type="text" value={item.description} onChange={(event) => updateQuoteItem(index, "description", event.target.value)} placeholder="Line item description" required />
                            <input aria-label="Quantity" type="number" min="1" value={item.quantity} onChange={(event) => updateQuoteItem(index, "quantity", event.target.value)} required />
                            <input aria-label="Unit price in USD" type="number" min="0.5" step="0.01" value={item.amount} onChange={(event) => updateQuoteItem(index, "amount", event.target.value)} placeholder="USD each" required />
                            {quote.items.length > 1 && <button type="button" onClick={() => setQuote((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))}>Remove</button>}
                          </div>
                        ))}
                      </div>
                      <button className="admin-inline-action" type="button" onClick={() => setQuote((current) => ({ ...current, items: [...current.items, { description: "", quantity: 1, amount: "" }] }))}><FaPlus /> Add line item</button>
                      <label>Quote notes<textarea value={quote.notes} onChange={(event) => setQuote((current) => ({ ...current, notes: event.target.value }))} /></label>
                      <button type="submit" disabled={busy === "quote"}>{busy === "quote" ? "Creating..." : "Create quote draft"}</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <div className="admin-panel-heading"><h3>3D design versions</h3><span>GLB + up to 8 renders</span></div>
                    <div className="admin-version-list">
                      {project.versions.map((item) => (
                        <article key={item.id}>
                          <div><strong>Version {item.versionNumber}: {item.title}</strong><em>{item.status}</em></div>
                          <p>{item.notes}</p>
                          {item.customerFeedback && <blockquote>{item.customerFeedback}</blockquote>}
                          <div className="admin-project-files">
                            {[item.glbFile, ...(item.renders || [])].filter(Boolean).map((file) => (
                              <button type="button" onClick={() => downloadFile(file)} key={file.id}><FaDownload /> {file.originalName}</button>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                    <form className="admin-version-form" onSubmit={uploadVersion}>
                      <h4>Publish a new version</h4>
                      <label>Version title<input type="text" value={version.title} onChange={(event) => setVersion((current) => ({ ...current, title: event.target.value }))} placeholder="e.g. Revised enclosure profile" required /></label>
                      <label>Design notes<textarea value={version.notes} onChange={(event) => setVersion((current) => ({ ...current, notes: event.target.value }))} /></label>
                      <label className="admin-file-picker"><FaUpload /> {version.glb ? version.glb.name : "Choose GLB model"}<input type="file" accept=".glb" onChange={(event) => setVersion((current) => ({ ...current, glb: event.target.files?.[0] || null }))} required /></label>
                      <label className="admin-file-picker"><FaUpload /> {version.renders.length ? `${version.renders.length} renders selected` : "Choose render images"}<input type="file" multiple accept=".png,.jpg,.jpeg,.webp" onChange={(event) => setVersion((current) => ({ ...current, renders: Array.from(event.target.files || []) }))} /></label>
                      <button type="submit" disabled={busy === "version"}>{busy === "version" ? "Uploading..." : "Publish design version"}</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>Project conversation</h3>
                    <div className="admin-message-list">
                      {project.messages.map((item) => <article key={item.id}><header><strong>{item.authorRole === "admin" ? "Design team" : item.authorName || "Customer"}</strong><span>{formatProjectDate(item.createdAt)}</span></header><p>{item.body}</p></article>)}
                    </div>
                    <form className="admin-message-form" onSubmit={sendMessage}>
                      <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Write a customer-visible message..." required />
                      <div><label className="admin-file-picker"><FaFileAlt /> {messageFile ? messageFile.name : "Attach file"}<input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.step,.stp,.dxf" onChange={(event) => setMessageFile(event.target.files?.[0] || null)} /></label><button type="submit" disabled={busy === "message"}><FaPaperPlane /> Send</button></div>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>Prototype shipping</h3>
                    <form className="admin-form-grid" onSubmit={updateShipping}>
                      <label>Carrier<input type="text" value={shipping.trackingCarrier} onChange={(event) => setShipping((current) => ({ ...current, trackingCarrier: event.target.value }))} /></label>
                      <label>Tracking number<input type="text" value={shipping.trackingNumber} onChange={(event) => setShipping((current) => ({ ...current, trackingNumber: event.target.value }))} required /></label>
                      <label>Tracking URL<input type="url" value={shipping.trackingUrl} onChange={(event) => setShipping((current) => ({ ...current, trackingUrl: event.target.value }))} /></label>
                      <label>Shipping note<input type="text" value={shipping.shippingNote} onChange={(event) => setShipping((current) => ({ ...current, shippingNote: event.target.value }))} /></label>
                      <button type="submit" disabled={busy === "shipping"}><FaBoxOpen /> Publish shipment</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>Notifications & timeline</h3>
                    <div className="admin-notifications">
                      {project.notifications.map((item) => (
                        <article key={item.id}><div><strong>{item.channel}: {item.subject}</strong><span>{item.status} · {item.attempts} attempts</span>{item.error && <small>{item.error}</small>}</div>{item.status !== "sent" && <button type="button" onClick={() => retryNotification(item.id)} disabled={Boolean(busy)}>Retry</button>}</article>
                      ))}
                    </div>
                    <ol className="admin-timeline">
                      {[...project.events].reverse().map((item) => <li key={item.id}><strong>{humanizeEvent(item.eventType)}</strong><span>{formatProjectDate(item.createdAt)}</span></li>)}
                    </ol>
                  </section>
                </>
              ) : null}
            </section>
          </div>
        </>
      )}
    </main>
  );
};

export default AdminCustomProjects;
